import React, { useState } from 'react';

import { Modal, useModal } from "src/components/Modal/Modal";
import ContextMenu from "src/components/ContextMenu/ContextMenu";
import { toast } from "src/components/Toast/ToastManager";
import Confirm from "src/components/Confirm/Confirm";

import { apiService } from "../../services";
import type { CustomCellRendererProps } from 'ag-grid-react';

import "./BalancesPayouts.scss";

interface IRefundRow {
  id: string;
  amount: string;
  status: string;
  created: string;
  chargeId: string;
  canCancel: boolean;
}

interface CustomRefundCellRendererProps extends CustomCellRendererProps {
  data: IRefundRow;
  refreshRefunds(): void;
  clubId: string;
}

enum RefundActionsMenuOptions {
  Cancel = "cancel",
}

export const RefundActionsRenderer = (props: CustomRefundCellRendererProps) => {
  const { isShown, toggle } = useModal();
  const [modalContent, setModalContent] = useState<RefundActionsMenuOptions | undefined>();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const cancelRefund = () => {
    setIsProcessing(true);

    apiService.cancelStripeRefund(props.clubId, props.data.id)
      .then(() => {
        toast.show({
          title: "Success",
          content: "Refund cancelled successfully",
          type: "success"
        });
        toggle();
        setModalContent(undefined);
        props.refreshRefunds();
      })
      .catch((error: any) => {
        console.error("Error cancelling refund:", error);
        
        // Extract error message from the response
        let errorMessage = "Failed to cancel refund. Please try again later.";
        
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        // Provide more specific error messages for common scenarios
        if (errorMessage.includes('cannot be canceled') || errorMessage.includes('already processed')) {
          errorMessage = "This refund has already been processed and cannot be cancelled. The funds have been returned to the customer.";
        } else if (errorMessage.includes('not found')) {
          errorMessage = "Refund not found. It may have already been cancelled or completed.";
        }
        
        toast.show({
          title: "Unable to Cancel Refund",
          content: errorMessage,
          type: "fail"
        });
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };


  const getModalContent = () => {
    switch (modalContent) {
      case RefundActionsMenuOptions.Cancel:
        return (
          <Confirm
            message={
              <div>
                <p>Are you sure you want to cancel this {props.data.amount} refund? This action cannot be undone.</p>
              </div>
            }
            onConfirm={cancelRefund}
            onCancel={() => {
              toggle();
              setModalContent(undefined);
            }}
            buttonsDisabled={isProcessing}
          />
        );
      default:
        return <div />;
    }
  };

  const getModalClassName = () => {
    switch (modalContent) {
      case RefundActionsMenuOptions.Cancel:
        return "confirmModal";
      default:
        return "";
    }
  };

  const menuOptions = [];

  // Add Cancel option for cancellable refunds
  if (props.data.canCancel) {
    menuOptions.push({
      label: "Cancel Refund",
      onClick: () => {
        toggle();
        setModalContent(RefundActionsMenuOptions.Cancel);
      }
    });
  }

  // Don't render anything if no options available
  if (menuOptions.length === 0) {
    return null;
  }

  return (
    <div className="ActionRenderer">
      <ContextMenu options={menuOptions} />
      <Modal
        className={getModalClassName()}
        isShown={isShown}
        hide={() => { 
          toggle(); 
          setModalContent(undefined); 
        }}
        modalContent={getModalContent()}
      />
    </div>
  );
};
